const ACTIVE_CLASS = 'is-active';
const initializedSections = new WeakSet();
const animationFrames = new WeakMap();
const transitionHandlers = new WeakMap();

const getFaqSections = (scope) => {
  if (!scope) {
    return [];
  }

  if (scope instanceof Element && scope.matches('.faq-sec')) {
    return [scope];
  }

  if (!scope.querySelectorAll) {
    return [];
  }

  return Array.from(scope.querySelectorAll('.faq-sec'));
};

const getItemParts = (item) => ({
  button: item.querySelector('.faq-sec__item-toggle'),
  content: item.querySelector('.faq-sec__item-content'),
});

const cancelHeightFrame = (content) => {
  const frame = animationFrames.get(content);

  if (frame) {
    window.cancelAnimationFrame(frame);
    animationFrames.delete(content);
  }
};

const requestHeightFrame = (content, callback) => {
  cancelHeightFrame(content);

  const frame = window.requestAnimationFrame(() => {
    animationFrames.delete(content);
    callback();
  });

  animationFrames.set(content, frame);
};

const clearTransitionHandler = (content) => {
  const handler = transitionHandlers.get(content);

  if (handler) {
    content.removeEventListener('transitionend', handler);
    transitionHandlers.delete(content);
  }
};

const setA11yState = (item, isActive) => {
  const { button, content } = getItemParts(item);

  if (button) {
    button.setAttribute('aria-expanded', isActive ? 'true' : 'false');
  }

  if (content) {
    content.setAttribute('aria-hidden', isActive ? 'false' : 'true');
  }
};

const prepareInitialState = (items) => {
  let activeItem = items.find((item) => item.classList.contains(ACTIVE_CLASS));

  if (!activeItem && items.length) {
    activeItem = items[0];
  }

  items.forEach((item) => {
    const { content } = getItemParts(item);
    const isActive = item === activeItem;

    item.classList.toggle(ACTIVE_CLASS, isActive);
    setA11yState(item, isActive);

    if (content) {
      content.style.height = isActive ? 'auto' : '0px';
    }
  });
};

const watchOpenTransition = (item, content) => {
  clearTransitionHandler(content);

  const handler = (event) => {
    if (event.target !== content || event.propertyName !== 'height') {
      return;
    }

    clearTransitionHandler(content);

    if (item.classList.contains(ACTIVE_CLASS)) {
      content.style.height = 'auto';
    }
  };

  transitionHandlers.set(content, handler);
  content.addEventListener('transitionend', handler);
};

const openItem = (item) => {
  const { button, content } = getItemParts(item);

  if (!button || !content || item.classList.contains(ACTIVE_CLASS)) {
    return;
  }

  clearTransitionHandler(content);
  item.classList.add(ACTIVE_CLASS);
  setA11yState(item, true);
  content.style.height = '0px';
  content.getBoundingClientRect();

  requestHeightFrame(content, () => {
    content.style.height = `${content.scrollHeight}px`;
    watchOpenTransition(item, content);
  });
};

const closeItem = (item) => {
  const { button, content } = getItemParts(item);

  if (!button || !content || !item.classList.contains(ACTIVE_CLASS)) {
    return;
  }

  clearTransitionHandler(content);
  cancelHeightFrame(content);
  content.style.height = `${content.scrollHeight}px`;
  content.getBoundingClientRect();
  item.classList.remove(ACTIVE_CLASS);
  setA11yState(item, false);

  requestHeightFrame(content, () => {
    content.style.height = '0px';
  });
};

const initFaqSection = (scope = document) => {
  const sections = getFaqSections(scope);

  sections.forEach((section) => {
    if (initializedSections.has(section)) {
      return;
    }

    const items = Array.from(section.querySelectorAll('.faq-sec__item'));

    if (!items.length) {
      return;
    }

    initializedSections.add(section);
    prepareInitialState(items);

    section.addEventListener('click', (event) => {
      const button = event.target.closest('.faq-sec__item-toggle');

      if (!button || !section.contains(button)) {
        return;
      }

      const currentItem = button.closest('.faq-sec__item');

      if (!currentItem) {
        return;
      }

      if (currentItem.classList.contains(ACTIVE_CLASS)) {
        closeItem(currentItem);
        return;
      }

      items.forEach((item) => {
        if (item !== currentItem) {
          closeItem(item);
        }
      });

      openItem(currentItem);
    });
  });
};

initFaqSection();
window.initFaqSection = initFaqSection;
